/**
 * Shared shapes + parsers for Availity coverage / eligibility tool results.
 * Snapshotted into Work chat artifacts (directory tools: apps.read / apps.write).
 */

export const ELIGIBILITY_LEAF_TOOLS = new Set<string>([
  'integrations.coverage.history',
  'integrations.coverage.get',
  'integrations.coverage.check',
]);

export const DIRECTORY_APP_TOOLS = new Set<string>([
  'integrations.apps.read',
  'integrations.apps.write',
]);

export interface EligibilityBenefitRow {
  code?: string;
  label?: string;
  amount?: string | number | boolean | null;
  remaining?: string | number | null;
  network?: string | boolean | null;
  serviceTypes?: unknown;
  authorizationRequired?: boolean | null;
  referralRequired?: boolean | null;
  timePeriod?: string | null;
}

export interface EligibilityPatientRow {
  id: string;
  status?: string;
  planStatus?: string;
  payer?: string;
  planName?: string;
  memberId?: string;
  patientFirstName?: string;
  patientLastName?: string;
  asOfDate?: string;
  coverageStart?: string;
  coverageEnd?: string;
  lastUpdated?: string;
  copay?: string | number | null;
  coinsurance?: string | number | null;
  deductible?: string | number | null;
  deductibleRemaining?: string | number | null;
  outOfPocketMax?: string | number | null;
  outOfPocketRemaining?: string | number | null;
  priorAuthRequired?: boolean | null;
  referralRequired?: boolean | null;
  messages?: string[];
  benefits?: EligibilityBenefitRow[];
}

export interface EligibilityPayload {
  provider?: string;
  query?: string;
  patients: EligibilityPatientRow[];
  warning?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const asBool = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

/** Keep legitimate ``0`` / ``0.0``; drop only null/undefined/empty string. */
const asAmount = (value: unknown): string | number | null | undefined => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return undefined;
};

export const directoryLeafTool = (
  toolName: string,
  input: unknown
): string | undefined => {
  if (!DIRECTORY_APP_TOOLS.has(toolName)) return toolName;
  if (!isRecord(input)) return undefined;
  return asString(input.tool);
};

export const isEligibilityLeafTool = (leaf: string | undefined): boolean =>
  Boolean(leaf && ELIGIBILITY_LEAF_TOOLS.has(leaf));

export const patientDisplayName = (row: EligibilityPatientRow): string => {
  const name = [row.patientFirstName, row.patientLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (name) return name;
  if (row.memberId) return `Member ${row.memberId}`;
  return `Coverage ${row.id}`;
};

export const formatEligibilityAmount = (
  value: string | number | boolean | null | undefined
): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return undefined;
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeBenefitRow = (raw: Record<string, unknown>): EligibilityBenefitRow => ({
  code: asString(raw.code) ?? undefined,
  label: asString(raw.label) ?? asString(raw.status) ?? asString(raw.name),
  amount: asAmount(raw.amount),
  remaining: asAmount(raw.remaining),
  network:
    typeof raw.network === 'boolean' || typeof raw.network === 'string'
      ? raw.network
      : undefined,
  serviceTypes: raw.service_types ?? raw.serviceTypes,
  authorizationRequired:
    asBool(raw.authorization_required) ?? asBool(raw.authorizationRequired),
  referralRequired: asBool(raw.referral_required) ?? asBool(raw.referralRequired),
  timePeriod: asString(raw.time_period) ?? asString(raw.timePeriod),
});

const fromBenefitsSummary = (
  summary: Record<string, unknown>,
  id: string
): EligibilityPatientRow => {
  const messagesRaw = summary.messages;
  const messages = Array.isArray(messagesRaw)
    ? messagesRaw
        .map((m) => (typeof m === 'string' ? m.trim() : asString(m)))
        .filter((m): m is string => Boolean(m))
    : undefined;
  const benefitsRaw = summary.benefits;
  const benefits = Array.isArray(benefitsRaw)
    ? benefitsRaw.filter(isRecord).map(normalizeBenefitRow)
    : undefined;

  return {
    id,
    status: asString(summary.status),
    planStatus: asString(summary.plan_status) ?? asString(summary.planStatus),
    payer: asString(summary.payer),
    planName: asString(summary.plan_name) ?? asString(summary.planName),
    memberId: asString(summary.member_id) ?? asString(summary.memberId),
    patientFirstName:
      asString(summary.patient_first_name) ?? asString(summary.patientFirstName),
    patientLastName:
      asString(summary.patient_last_name) ?? asString(summary.patientLastName),
    asOfDate: asString(summary.as_of_date) ?? asString(summary.asOfDate),
    coverageStart: asString(summary.coverage_start) ?? asString(summary.coverageStart),
    coverageEnd: asString(summary.coverage_end) ?? asString(summary.coverageEnd),
    lastUpdated: asString(summary.last_updated) ?? asString(summary.lastUpdated),
    copay: asAmount(summary.copay),
    coinsurance: asAmount(summary.coinsurance),
    deductible: asAmount(summary.deductible),
    deductibleRemaining:
      asAmount(summary.deductible_remaining) ?? asAmount(summary.deductibleRemaining),
    outOfPocketMax:
      asAmount(summary.out_of_pocket_max) ?? asAmount(summary.outOfPocketMax),
    outOfPocketRemaining:
      asAmount(summary.out_of_pocket_remaining) ?? asAmount(summary.outOfPocketRemaining),
    priorAuthRequired:
      asBool(summary.prior_auth_required) ?? asBool(summary.priorAuthRequired),
    referralRequired:
      asBool(summary.referral_required) ?? asBool(summary.referralRequired),
    messages,
    benefits,
  };
};

const fromHistorySummary = (
  raw: Record<string, unknown>,
  index: number
): EligibilityPatientRow | null => {
  const id =
    asString(raw.id) ??
    asString(raw.coverage_id) ??
    asString(raw.coverageId) ??
    `coverage-${index}`;
  return {
    id,
    status: asString(raw.status),
    planStatus: asString(raw.plan_status) ?? asString(raw.planStatus),
    payer: asString(raw.payer),
    asOfDate: asString(raw.as_of_date) ?? asString(raw.asOfDate),
    lastUpdated: asString(raw.last_updated) ?? asString(raw.lastUpdated),
    patientFirstName: asString(raw.patient_first_name) ?? asString(raw.patientFirstName),
    patientLastName: asString(raw.patient_last_name) ?? asString(raw.patientLastName),
  };
};

/**
 * Parse Availity coverage history / get / check result metadata into a
 * clinician-facing eligibility batch payload.
 */
export const parseEligibilityPayload = (
  data: unknown,
  input?: unknown
): EligibilityPayload | null => {
  if (!isRecord(data)) return null;

  const provider = asString(data.provider);
  const query =
    asString(data.query) ??
    (isRecord(input) ? (asString(input.query) ?? asString(input.q)) : undefined);
  const warning = asString(data.warning);

  const coverages = data.coverages;
  if (Array.isArray(coverages)) {
    const patients = coverages
      .filter(isRecord)
      .map((row, index) => fromHistorySummary(row, index))
      .filter((row): row is EligibilityPatientRow => row !== null);
    if (patients.length === 0 && !warning && !provider) return null;
    return { provider, query, patients, warning };
  }

  const benefitsSummary = data.benefits_summary ?? data.benefitsSummary;
  if (isRecord(benefitsSummary)) {
    const id =
      asString(data.coverage_id) ??
      asString(data.coverageId) ??
      (isRecord(data.coverage) ? asString(data.coverage.id) : undefined) ??
      'coverage';
    return {
      provider,
      query,
      patients: [fromBenefitsSummary(benefitsSummary, id)],
      warning,
    };
  }

  // Bare coverage object without benefits_summary (defensive).
  if (isRecord(data.coverage)) {
    const coverage = data.coverage;
    const id =
      asString(data.coverage_id) ??
      asString(data.coverageId) ??
      asString(coverage.id) ??
      'coverage';
    const row = fromHistorySummary(
      {
        id,
        status: coverage.status ?? data.status,
        plan_status: coverage.planStatus,
        payer: isRecord(coverage.payer) ? coverage.payer.name : coverage.payer,
        patient_first_name: isRecord(coverage.patient)
          ? coverage.patient.firstName
          : undefined,
        patient_last_name: isRecord(coverage.patient)
          ? coverage.patient.lastName
          : undefined,
      },
      0
    );
    return row ? { provider, query, patients: [row], warning } : null;
  }

  return null;
};
